import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi, buildQueryString, ApiClientError } from "@/lib/api/client";
import { recipientsFetch } from '@/lib/recipients/api';
import { toast } from "sonner";
import { importKeys, recipientKeys } from "@/hooks/api/query-keys";
import type {
  ImportHistory,
  ImportFileSummary,
  FieldMapping,
} from "@/lib/types/recipients";
import {
  IMPORT_FILE_SUMMARY_INVALID,
  type ImportFileSummaryPayload,
} from "@/components/recipients/import/import-file-summary";
import type { PaginatedResult } from "@/hooks/api/pagination";
import { lmsLearnerKeys } from "@/components/lms/hooks/lms-learner-keys";

/**
 * The course Enrol dialog's roster (the LMS learner list: one row per learner,
 * with name, department and groups) no longer re-reads on open, so every
 * recipient change marks it stale: a learner created, imported, edited or
 * deleted here reaches the dialog without a reload.
 */
function invalidateLmsLearners(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: lmsLearnerKeys.all() });
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * GET /recipients/import/history (ListRecipientImportsResponseDto): the page,
 * plus `recipientsImported` — the SUM of successCount over every import the
 * filters match, rolled-back imports left out (SI15, audit 2026-09-25) — and,
 * #830 Part B, `statusCounts`: how many of those imports are in each status,
 * from the same GROUP BY status (every status a key; the values add up to
 * `total`). Import History's success rate is read from it.
 */
interface ImportHistoryListResponse<T> extends PaginatedResponse<T> {
  recipientsImported?: number;
  statusCounts?: unknown;
}

/** Imports per status, as the API counted them (lib/recipients/import-success-rate.ts reads it). */
export type ImportStatusCounts = Readonly<Record<string, number>>;

/**
 * A page of import history, how many recipients all matching imports brought
 * in, and how many of them are in each status. `statusCounts` is absent (never
 * zeros) when the API did not send it.
 */
export type ImportHistoryPage = PaginatedResult<ImportHistory> & {
  recipientsImported?: number;
  statusCounts?: ImportStatusCounts;
};

// BE DTO uses successRows/errorRows/duplicateRows/skipRows; FE typed
// interface uses successCount/errorCount/duplicateCount/skipCount.
// Normalize so every callsite keeps working regardless of which the
// server emits. #830 — `pendingReviewCount` (rows still waiting in Pending
// Actions) is absent from an API that predates it: read it as 0, never
// undefined, so "Review N waiting rows" is hidden rather than broken.
// #830 Part 2 — `fileSummary` likewise: null unless it is four whole numbers.
type RawImportHistory = Omit<
  ImportHistory,
  "pendingReviewCount" | "fileSummary"
> & {
  pendingReviewCount?: number;
  fileSummary?: unknown;
  successRows?: number;
  errorRows?: number;
  duplicateRows?: number;
  skipRows?: number;
};

const isCount = (n: unknown): n is number =>
  typeof n === "number" && Number.isSafeInteger(n) && n >= 0;

/**
 * #830 Part 2 — the wizard's file summary, or null. An import without one (an
 * older import, an AD Syncer run, an API that predates it) and a malformed one
 * both read null, so every screen falls back to what it showed before rather
 * than to zeros that would claim the wizard removed nothing.
 */
export function normalizeFileSummary(raw: unknown): ImportFileSummary | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const {
    rowsInFile,
    removedBeforeImport,
    unreadableInWizard,
    duplicatesSkippedInWizard,
  } = r;
  if (
    !isCount(rowsInFile) ||
    !isCount(removedBeforeImport) ||
    !isCount(unreadableInWizard) ||
    !isCount(duplicatesSkippedInWizard)
  ) {
    return null;
  }
  return {
    rowsInFile,
    removedBeforeImport,
    unreadableInWizard,
    duplicatesSkippedInWizard,
  };
}

function normalizeImport(raw: RawImportHistory): ImportHistory {
  return {
    ...raw,
    successCount: raw.successCount ?? raw.successRows ?? raw.successfulRows ?? 0,
    errorCount: raw.errorCount ?? raw.errorRows ?? raw.failedRows ?? 0,
    duplicateCount: raw.duplicateCount ?? raw.duplicateRows ?? 0,
    skipCount: raw.skipCount ?? raw.skipRows ?? 0,
    pendingReviewCount:
      typeof raw.pendingReviewCount === "number" ? raw.pendingReviewCount : 0,
    fileSummary: normalizeFileSummary(raw.fileSummary),
  };
}

// ============ Query Hooks ============

/** Unpaginated — returns all import history as a flat array. */
export function useImportHistory() {
  return useQuery({
    queryKey: importKeys.history(),
    queryFn: async () => {
      const result = await fetchApi<
        { data: RawImportHistory[] } | RawImportHistory[]
      >("/recipients/import/history");
      const list = Array.isArray(result) ? result : result.data;
      return list.map(normalizeImport);
    },
  });
}

/**
 * Paginated — returns { data, total, page, limit, totalPages }. Used by the
 * import history table and the Pending Actions import panel.
 *
 * While a listed import is running the query polls every 2 s. `options.
 * idleRefetchInterval` (ms) also polls when none is, so a screen that must
 * notice an import started elsewhere (an AD Syncer run, another tab) does;
 * without it the query refetches only when invalidated. `options.enabled`
 * false asks for nothing.
 *
 * `source` (#830) narrows to CSV/Excel uploads or AD Syncer runs on the
 * server, before the page is cut.
 */
export function useImportHistoryPaginated(
  filter?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string[];
    errors?: string;
    source?: "CSV" | "AD Syncer";
    from?: string;
    to?: string;
  },
  options?: { idleRefetchInterval?: number; enabled?: boolean },
) {
  const defaultLimit = filter?.limit ?? 25;
  const idleRefetchInterval = options?.idleRefetchInterval ?? false;
  return useQuery({
    queryKey: [...importKeys.all(), "paginated", filter],
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => {
      const raw = query.state.data as
        | { data?: Array<{ status?: string }> }
        | Array<{ status?: string }>
        | undefined;
      const rows = (Array.isArray(raw) ? raw : raw?.data) ?? [];
      const arr = Array.isArray(rows) ? rows : [];
      const inFlight = arr.some(
        (r) =>
          r.status === "Pending" ||
          r.status === "Processing" ||
          r.status === "Analyzing",
      );
      return inFlight ? 2_000 : idleRefetchInterval;
    },
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const qs = filter
        ? buildQueryString(filter as Record<string, unknown>)
        : "";
      return fetchApi<
        ImportHistoryListResponse<RawImportHistory> | RawImportHistory[]
      >(`/recipients/import/history${qs}`);
    },
    select: (raw): ImportHistoryPage => {
      if (Array.isArray(raw)) {
        return {
          data: raw.map(normalizeImport),
          total: raw.length,
          page: 1,
          limit: raw.length || defaultLimit,
          totalPages: 1,
        };
      }
      const r = raw as ImportHistoryListResponse<RawImportHistory>;
      const limit = r.limit ?? defaultLimit;
      const total = r.total ?? 0;
      return {
        data: (r.data ?? []).map(normalizeImport),
        total,
        page: r.page ?? 1,
        limit,
        totalPages: r.totalPages ?? Math.max(1, Math.ceil(total / limit)),
        ...(typeof r.recipientsImported === "number"
          ? { recipientsImported: r.recipientsImported }
          : {}),
        ...(r.statusCounts &&
        typeof r.statusCounts === "object" &&
        !Array.isArray(r.statusCounts)
          ? { statusCounts: r.statusCounts as ImportStatusCounts }
          : {}),
      };
    },
  });
}

export function useImportStatus(id: string | undefined) {
  return useQuery({
    queryKey: importKeys.status(id!),
    queryFn: async () =>
      normalizeImport(
        await recipientsFetch<RawImportHistory>(`import/${id}/status`),
      ),
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll while import is in progress
      const status = query.state.data?.status;
      if (
        status === "Processing" ||
        status === "Pending" ||
        status === "Analyzing"
      )
        return 2000;
      return false;
    },
  });
}

// ============ Mutation Hooks ============

export function useAnalyzeImport() {
  return useMutation({
    mutationFn: (data: { file: string; fileName: string }) =>
      recipientsFetch<{
        csvHeaders: string[];
        sampleRows: Record<string, string>[];
        suggestedMappings: FieldMapping[];
        totalRows: number;
      }>("import/analyze", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useProcessImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      rows: Record<string, unknown>[];
      fileName: string;
      fileSize?: number;
      mimeType?: string;
      fieldMappings: FieldMapping[];
      targetGroupIds?: string[];
      targetTagIds?: string[];
      duplicateHandling?: "Skip" | "Update";
      duplicateDetectionFields?: string[];
      targetDomainId?: string;
      // #776 — status for new (or restored) people whose row has no Status.
      defaultStatus?: "Active" | "Inactive";
      // #830 Part 2 — what the wizard did with every row it did not send.
      fileSummary?: ImportFileSummaryPayload;
      // PR #34 review — each row's zero-based row in the file, in the order
      // of `rows`, so the import's errors name the file's rows.
      fileRowIndexes?: number[];
    }) =>
      recipientsFetch<ImportHistory>("import", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: importKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) => {
      // #830 Part 2 — a refused file summary is explained by the wizard, in
      // the reader's language; the API's message is English and technical.
      if (
        err instanceof ApiClientError &&
        err.errorCode === IMPORT_FILE_SUMMARY_INVALID
      ) {
        return;
      }
      toast.error(err.message);
    },
  });
}

/**
 * Undo an import (SI14, audit 2026-09-25). The answer is what
 * `RecipientImportService.rollbackImport` returns: how many recipients moved to
 * the Recycle Bin, and the import's id and file name. On success the imports
 * AND the recipients caches are invalidated — the people the undo moved are
 * gone from the Recipients list. The caller names the import in its own
 * success and failure messages, so this hook does not toast.
 */
export function useRollbackImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<{ affected: number; id: string; name: string }>(
        `import/${id}/rollback`,
        {
          method: "POST",
        },
      ),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: importKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}
